import React, {useMemo, useState} from 'react';
import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem as MuiMenuItem,
  MenuItemProps as MuiMenuItemProps,
  Skeleton as MuiSkeleton,
  Popover,
  Stack,
  styled,
} from '@mui/material';
import {
  Avatar,
  Body,
  Icon,
  designSystemToken,
} from '@lightricks/react-design-system';
import {ROUTES} from '@contentlab/config/routesConstants';
import useContactSales from '@contentlab/hooks/use-contact-sales';
import useContentLabPageName from '@contentlab/hooks/use-content-lab-page-name';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import Fortress from '@/services/fortress/Fortress';
import {
  CONTENT_STUDIO_PRICING_SOURCES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import getInitials from '@/utils/getInitials';
import queryClient from '@/utils/reactQueryClient';
import translate from '@/utils/translate';
import {DashboardUser} from '@/types/dashboardUser';
import useBrandsQuery from '@/hooks/queries/use-brands-query/useBrandsQuery';
import useBrandId from '@/hooks/use-brand-id';
import useNavigation from '@/hooks/use-navigation';
import useToken from '@/hooks/use-token';
import {UserMenuItem, staticUserMenuItems} from './userMenuItems';

interface UserMenuProps {
  testID?: string;
  user: DashboardUser;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  onItemClick?: (userMenuItem: UserMenuItem) => void;
}

interface MenuItemProps extends MuiMenuItemProps {
  'data-testid'?: string;
  'href'?: string;
  'target'?: string;
}

const UserMenuContainer = styled(Box)<{open: boolean}>(({open}) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: open ? 'space-between' : 'unset',
  width: '100%',
  position: 'relative',
  background: 'transparent',
  cursor: 'pointer',
  height: '36px',
}));

const UserMenuStack = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  width: 'auto',
  padding: '0 8px',
}));

const UserMenuText = styled(Box)<{open: boolean}>(({open}) => ({
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  maxWidth: open ? 148 : 0,
  opacity: open ? 1 : 0,
  paddingLeft: open ? 8 : 0,
  transition:
    'max-width 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.2s, margin-left 0.3s, padding-left 0.3s',
  overflow: 'hidden',
}));

const UserName = styled(Body)(() => ({
  color: designSystemToken('semantic.fg.primary'),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const BrandName = styled(Body)(() => ({
  color: designSystemToken('semantic.fg.secondary'),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const PopupPaper = styled(Box)(() => ({
  background: designSystemToken('semantic.bg.white'),
  borderRadius: 24,
  boxShadow:
    '0px 4px 12px -2px rgba(24, 39, 75, 0.08), 0px 0px 4px 0px rgba(24, 39, 75, 0.02)',
  minWidth: 264,
  padding: '8px 0',
}));

function UserMenuItemRow({
  item,
  onClick,
}: {
  item: UserMenuItem;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
}) {
  const isExternal = !!item.link && item.external;
  const menuItemProps: MenuItemProps = {
    'key': item.id,
    'data-testid': item.testID,
    'onClick': onClick,
    'sx': {
      'display': 'flex',
      'alignItems': 'center',
      'minHeight': 48,
      'paddingInlineStart': '28px',
      'color': item.isDanger
        ? designSystemToken('semantic.fg.danger')
        : designSystemToken('semantic.fg.primary'),
      ':hover': {
        background: designSystemToken('semantic.bg.neutral-inverse'),
      },
    },
  };
  if (isExternal) {
    menuItemProps.component = 'a';
    menuItemProps.href = item.link;
    menuItemProps.target = '_blank';
    menuItemProps.rel = 'noopener noreferrer';
  }
  if (item.isDivider) {
    return (
      <Divider
        key={item.id}
        sx={{
          margin: '8px 16px',
          backgroundColor: designSystemToken('semantic.bg.tertiary'),
        }}
      />
    );
  }
  return (
    <MuiMenuItem {...menuItemProps}>
      {item.icon && (
        <ListItemIcon>
          <Icon
            name={item.icon}
            size="medium"
            appearance={item.isDanger ? 'danger' : 'neutral'}
          />
        </ListItemIcon>
      )}
      <ListItemText>{translate(item.labelLocaleKey)}</ListItemText>
      {item.children && (
        <ListItemIcon>
          <Icon
            name="Actions-Arrow-Forward-Small"
            size="medium"
            appearance="neutral"
            style={{width: 28, height: 28}}
          />
        </ListItemIcon>
      )}
      {item.endIcon && !item.children && (
        <ListItemIcon>
          <Icon
            name={item.endIcon}
            size="medium"
            appearance="neutral"
            color={designSystemToken('semantic.fg.primary')}
          />
        </ListItemIcon>
      )}
    </MuiMenuItem>
  );
}

function RecursiveMenu({
  items,
  onClose,
  parentAnchorEl,
  open,
  parentMenu,
  initials,
  userName,
  brandName,
  logout,
  onItemClick,
}: {
  items: UserMenuItem[];
  onClose: () => void;
  parentAnchorEl: HTMLElement | null;
  open: boolean;
  parentMenu?: boolean;
  initials?: string;
  userName?: string;
  brandName?: string;
  logout?: () => void;
  onItemClick?: (userMenuItem: UserMenuItem) => void;
}) {
  const pageName = useContentLabPageName();
  const onContactSalesClick = useContactSales({
    flowName: 'user-menu',
    interactionLocation: `user-menu--${pageName}`,
  });

  const [submenuAnchor, setSubmenuAnchor] = useState<null | HTMLElement>(null);
  const [submenuItems, setSubmenuItems] = useState<UserMenuItem[] | null>(null);
  const {navigate} = useNavigation();

  const handleItemClick = (
    item: UserMenuItem,
    event: React.MouseEvent<HTMLElement>
  ) => {
    onItemClick?.(item);
    if (item.children) {
      setSubmenuAnchor(event.currentTarget);
      setSubmenuItems(item.children);
    } else {
      // TODO: Handle item click actions: Settings, Account, Submit a request
      if (item.onClick) {
        item.onClick();
      }
      if (item.id === 'logout' && logout) {
        logout();
      }
      if (item.id === 'manage-account') {
        navigate(`${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_PREFERENCES}`);
      }
      if (item.id === 'contact-sales') {
        onContactSalesClick();
      }
      if (item.id === 'pricing-and-plan') {
        AnalyticsService.createFlow(FLOW_NAMES.CONTENT_STUDIO.PRICING, {
          source: CONTENT_STUDIO_PRICING_SOURCES.USER_MENU,
          screen_presentation_id:
            AnalyticsService.getActiveScreenPresented().screen_presentation_id,
        });
        navigate(`${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_PRICING}`);
      }
      onClose();
    }
  };

  const handleSubmenuClose = () => {
    setSubmenuAnchor(null);
    setSubmenuItems(null);
  };

  return (
    <Popover
      open={open}
      anchorEl={parentAnchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: parentMenu ? 'right' : 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: parentMenu ? 'left' : 'center',
      }}
      slotProps={{
        paper: {
          style: {
            borderRadius: 24,
            minWidth: 264,
            boxShadow: '0px 8px 32px rgba(44, 45, 48, 0.12)',
            transform: `translateY(${parentMenu ? 0 : -80}px)`,
          },
        },
      }}
    >
      <PopupPaper>
        {!parentMenu && (
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            padding="16px 24px 8px 24px"
          >
            <Avatar
              size="sm"
              type="initials"
              initials={initials}
              background="#c6c1b4" // TODO: Use a design system token for background color
            />
            <Box marginLeft="16px" minWidth={0} flex={1}>
              <UserName size="md" sx={{fontWeight: 600}}>
                {userName}
              </UserName>
              <BrandName size="md">{brandName}</BrandName>
            </Box>
          </Box>
        )}
        {!parentMenu && (
          <Divider
            sx={{
              margin: '8px 16px',
              backgroundColor: designSystemToken('semantic.bg.tertiary'),
            }}
          />
        )}
        {items.map((item) => (
          <UserMenuItemRow
            key={item.id}
            item={item}
            onClick={(e) => handleItemClick(item, e)}
          />
        ))}
      </PopupPaper>
      {submenuItems && submenuAnchor && (
        <RecursiveMenu
          items={submenuItems}
          onClose={handleSubmenuClose}
          parentAnchorEl={submenuAnchor}
          open={Boolean(submenuAnchor)}
          parentMenu
          onItemClick={onItemClick}
        />
      )}
    </Popover>
  );
}

function BrandElementSkeleton() {
  return (
    <Stack direction="row" alignItems="center" gap="8px">
      <MuiSkeleton variant="circular" width="32px" height="32px" />
      <Stack direction="column" gap="2px">
        <MuiSkeleton variant="rectangular" height="18px" width="80px" />
        <MuiSkeleton variant="rectangular" height="18px" width="90px" />
      </Stack>
    </Stack>
  );
}

function UserMenu({
  testID = 'user-menu',
  user,
  open = true,
  onOpen,
  onClose,
  onItemClick,
}: UserMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const {resetToken} = useToken();
  const navigation = useNavigation();

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setPopupOpen(true);
    onOpen?.();
  };
  const handlePopupClose = () => {
    setPopupOpen(false);
    setAnchorEl(null);
    onClose?.();
  };

  const logout = async () => {
    setAnchorEl(null);
    Fortress.clearCookieTokens();
    await resetToken();
    queryClient.clear();
    const redirectTo = `${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_LOGIN}`;
    await navigation.navigate(redirectTo, undefined, false);
  };

  const brandId = useBrandId();
  const {data: brandsData, isFetched: isBrandsDataFetched} = useBrandsQuery({
    brandId,
  });

  const brands = useMemo(() => brandsData || [], [brandsData]);

  const selectedBrand = useMemo(
    () =>
      brands.find(
        (brand: {id: string}) => String(brand.id) === String(brandId)
      ),
    [brands, brandId]
  );

  const userInfo = useMemo(() => {
    const combinedName = [user.firstName, user.lastName]
      .filter(Boolean)
      .join(' ');

    if (user.displayName === combinedName && user.firstName === user.lastName) {
      // For the case of the new signup with only email, we want to show the firstName as the userName since the displayName,
      // the firstName and the lastName are the same
      return {
        initials: getInitials(user.firstName || '').toUpperCase(),
        userName: user.firstName,
        brandName: selectedBrand?.displayName || '',
      };
    }

    const userName =
      user.displayName ||
      (user.firstName !== user.lastName ? combinedName : user.firstName || '');
    const brandName = selectedBrand?.displayName || '';
    return {
      initials: getInitials(userName).toUpperCase(),
      userName,
      brandName,
    };
  }, [user, selectedBrand]);

  if (brands.length < 1 && !isBrandsDataFetched) {
    return <BrandElementSkeleton />;
  }

  return (
    <>
      <UserMenuContainer
        data-testid={testID}
        open={open}
        onClick={handleMenuClick}
      >
        <UserMenuStack>
          <Avatar
            size="sm"
            type="initials"
            initials={userInfo.initials}
            background="#c6c1b4"
            sx={{width: '32px'}}
          />
          <UserMenuText open={open}>
            <UserName size="md">{userInfo.userName}</UserName>
            <BrandName size="md">{userInfo.brandName}</BrandName>
          </UserMenuText>
        </UserMenuStack>
      </UserMenuContainer>
      <RecursiveMenu
        items={staticUserMenuItems}
        onClose={handlePopupClose}
        parentAnchorEl={anchorEl}
        open={popupOpen}
        initials={userInfo.initials}
        userName={userInfo.userName}
        brandName={userInfo.brandName}
        logout={logout}
        onItemClick={onItemClick}
      />
    </>
  );
}

export default UserMenu;
